/**
 * Přístupný tooltip s vysvětlením.
 *
 * Otevře se na hover i na focus (klávesnice) a zavře na blur / Escape / mouse
 * leave. Text je vždy dostupný přes `aria-describedby`, takže ho přečte i čtečka
 * — ne jen nativní `title`, který z klávesnice nejde vyvolat a čtečky ho čtou
 * nespolehlivě.
 *
 * Použití: malá „?" ikona vedle popisku (typicky v záhlaví sloupce tabulky).
 */
import { useId, useState, type ReactNode } from 'react';

export interface InfoTooltipProps {
  /** vysvětlující text (nebo bohatší obsah) */
  content: ReactNode;
  /** popisek pro čtečku, pokud spouštěč nemá vlastní text (default „Vysvětlení") */
  label?: string;
}

export function InfoTooltip({ content, label = 'Vysvětlení' }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <span
      className="info-tip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="info-tip__trigger"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        onClick={(e) => {
          // Nesmí spustit řazení sloupce, na kterém ikona sedí.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        ?
      </button>
      {open ? (
        <span role="tooltip" id={id} className="info-tip__bubble">
          {content}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Záhlaví sloupce s popiskem a tooltipem. Klik na text řadí (skrz `th`),
 * klik na „?" jen otevře vysvětlení.
 */
export function HeaderWithTip({ label, tip }: { label: string; tip: ReactNode }) {
  return (
    <span className="th-with-tip">
      {label}
      <InfoTooltip content={tip} label={`Co znamená sloupec ${label}`} />
    </span>
  );
}
